import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/apiClient";
import { useT } from "../../i18n/LanguageProvider";
import type {
  BouquetBuildDetail,
  BouquetBuildPhotoEntry,
  CustomerOrder,
  CustomerOrderEventView,
  OrderPaymentMethod,
} from "../../api/types";
import { afterCostLabel, bouquetCostText } from "../../utils/unknownCost";
import { Modal } from "../Modal";
import { OrderActions } from "./OrderActions";
import { useOrderReferencePhotos } from "./useOrderReferencePhotos";
import { WebOrderMoney } from "./WebOrderMoney";
import {
  deliveryJobStatusClass,
  deliveryJobStatusLabel,
  fulfillmentLabel,
  orderSlotLabel,
  orderStatusClass,
  orderStatusLabel,
} from "./orderUi";

/** Human labels for the order's timeline event types (G-20). */
const EVENT_LABELS: Record<string, string> = {
  CREATED: "Order created",
  CONFIRMED: "Confirmed",
  PREP_STARTED: "Preparation started",
  READY: "Marked ready",
  PAYMENT_TAKEN: "Payment taken",
  PICKUP_CONFIRMED: "Handover confirmed by the shop",
  COLLECTED: "Customer collected",
  DELIVERED: "Delivered",
  COMPLETED: "Completed — sale recorded",
  CANCELLED: "Cancelled",
  SALE_LINKED: "Sale linked",
  AMENDED: "Changed from the request",
};

/** How a payment row was paid. ONLINE (G-152) is a web customer's Shopify payment. */
const PAYMENT_METHOD_LABELS: Record<OrderPaymentMethod, string> = {
  CASH: "by cash",
  CARD: "by card",
  BANK_TRANSFER: "by bank transfer",
  ONLINE: "Online",
};

/** OD-32: the AMENDED event's figures, readable in place. */
function amendedDetail(raw: unknown): string {
  if (!raw || typeof raw !== "object") return "";
  const metadata = raw as Record<string, unknown>;
  if (metadata.kind === "PRICE" || metadata.kind === "LINKED_SALE_TOTAL") {
    const agreed = metadata.agreedTotalEur ?? "—";
    const actual = metadata.chargedTotalEur ?? metadata.saleTotalEur ?? "—";
    return ` — agreed ${agreed} €, charged ${actual} €`;
  }
  // G-147: goods the customer added at the counter handover.
  if (metadata.kind === "ADDED_AT_HANDOVER" && Array.isArray(metadata.lines)) {
    return (
      " — added at the counter: " +
      (metadata.lines as Array<Record<string, unknown>>)
        .map((l) => `${l.name} × ${l.quantity} (${l.lineTotalEur} €)`)
        .join(", ")
    );
  }
  if (metadata.kind === "RECIPE" && Array.isArray(metadata.differences)) {
    return (
      " — " +
      (metadata.differences as Array<Record<string, unknown>>)
        .map(
          (d) => `${d.itemName}: ${d.requestedQty} → ${d.builtQty}`,
        )
        .join(", ")
    );
  }
  return "";
}

/** G-152: a payment the Shopify poll booked (no person took it). */
function viaShopify(raw: unknown): boolean {
  return (
    raw != null &&
    typeof raw === "object" &&
    (raw as Record<string, unknown>).via === "SHOPIFY"
  );
}

type GalleryItem = {
  key: string;
  label: "Reference" | "Florist" | "Courier — delivery proof";
  url: string | null;
  by: string | null;
};

/**
 * G-16 — THE order-detail surface (owner: "when clicked it should show all
 * info including all timestamps and photos"). One canonical modal reached
 * from Orders history rows and the day modal's "Full detail" button:
 * every photo labeled by its source (Reference / Florist / Courier), the
 * bouquet's exact recipe (florists see composition, never money — the API
 * strips the fields, this component only renders what arrives), the full
 * event trail, the payment ledger, and the shared action row.
 */
export function OrderDetailModal({
  order,
  onClose,
  onChanged,
}: {
  order: CustomerOrder;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useT();
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<CustomerOrderEventView[] | null>(null);
  const [build, setBuild] = useState<BouquetBuildDetail | null>(null);
  const [buildPhotos, setBuildPhotos] = useState<
    Array<BouquetBuildPhotoEntry & { url: string | null }>
  >([]);
  const referenceUrls = useOrderReferencePhotos(order.id, "crm");

  useEffect(() => {
    let stale = false;
    const urls: string[] = [];
    void api
      .listOrderEvents(order.id)
      .then((rows) => !stale && setEvents(rows))
      .catch(() => !stale && setEvents([]));
    if (order.bouquetBuild) {
      const buildId = order.bouquetBuild.id;
      void api
        .getBouquetBuildDetail(buildId)
        .then((b) => !stale && setBuild(b))
        .catch(() => undefined);
      void api
        .listBouquetBuildPhotos(buildId)
        .then(async (entries) => {
          const withUrls = await Promise.all(
            entries.map(async (entry) => {
              let url: string | null = null;
              if (entry.origin === "BUILD" && entry.id) {
                url = await api.fetchBouquetBuildPhotoBlobUrl(buildId, entry.id);
              } else if (
                entry.origin === "DELIVERY_PROOF" &&
                entry.deliveryJobId
              ) {
                url = await api.fetchDeliveryProofBlobUrl(entry.deliveryJobId);
              }
              if (url) urls.push(url);
              return { ...entry, url };
            }),
          );
          if (!stale) setBuildPhotos(withUrls);
        })
        .catch(() => undefined);
    }
    return () => {
      stale = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [order.id, order.bouquetBuild]);

  const gallery: GalleryItem[] = useMemo(() => {
    const items: GalleryItem[] = referenceUrls.map((url, i) => ({
      key: `ref-${i}`,
      label: "Reference",
      url,
      by: null,
    }));
    for (const photo of buildPhotos) {
      items.push({
        key: photo.id ?? `proof-${photo.deliveryJobId}`,
        label:
          photo.origin === "BUILD" ? "Florist" : "Courier — delivery proof",
        url: photo.url,
        by: photo.uploadedBy?.fullName ?? null,
      });
    }
    return items;
  }, [referenceUrls, buildPhotos]);

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  const seesRecipeMoney = build != null && "totalCogsEur" in build;

  return (
    <Modal title={`${order.code} · ${order.customerName}`} onClose={onClose}>
      <div className="order-detail">
        {error ? <div className="alert error">{error}</div> : null}

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="order-detail-head">
          <span className={orderStatusClass(order.status)}>
            {orderStatusLabel(t, order.status)}
          </span>
          {order.deliveryJob ? (
            <span className={deliveryJobStatusClass(order.deliveryJob.status)}>
              {deliveryJobStatusLabel(t, order.deliveryJob.status)}
            </span>
          ) : null}
          {/* OD-32: reality diverged from the request — normal business,
              visible at a glance; the timeline holds the figures. */}
          {order.divergedFromRequest ? (
            <span className="badge warning">differs from request</span>
          ) : null}
          <span className="muted">
            {fulfillmentLabel(t, order.fulfillment)} · {orderSlotLabel(t, order)} ·{" "}
            {order.location.name}
            {order.source !== "MANUAL" ? ` · ${order.source.toLowerCase()}` : ""}
          </span>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          {order.customerPhone ? `${order.customerPhone} · ` : ""}
          {order.customerEmail ? `${order.customerEmail} · ` : ""}
          logged by {order.createdBy?.fullName ?? "—"}
          {order.preparedBy ? ` · prepared by ${order.preparedBy.fullName}` : ""}
        </p>
        {order.customerNotes ? (
          <p style={{ margin: 0 }}>{order.customerNotes}</p>
        ) : null}
        {order.bouquetName || order.bouquetNotes ? (
          <p style={{ margin: 0 }}>
            {order.bouquetName ? <strong>{order.bouquetName}</strong> : null}
            {order.bouquetName && order.bouquetNotes ? " — " : ""}
            {order.bouquetNotes}
          </p>
        ) : null}

        {/* G-112: finished bouquets from stock this order holds. */}
        {order.lines.some((line) => line.role === "READY_BOUQUET") ? (
          <p style={{ margin: 0 }}>
            <strong>Ready-made bouquets:</strong>{" "}
            {order.lines
              .filter((line) => line.role === "READY_BOUQUET")
              .map(
                (line) =>
                  `${line.bouquetBuild?.name || line.description}${
                    line.unitPriceEur != null ? ` (${line.unitPriceEur} €)` : ""
                  }`,
              )
              .join(", ")}
          </p>
        ) : null}

        {/* ── Money ──────────────────────────────────────────────── */}
        <div className="order-detail-money">
          <span>
            {/* G-112 (OD-100): the fee is owed through the same ledger, so
                it is named here or "outstanding" would not add up. */}
            {order.totalEur
              ? `Agreed ${order.totalEur} €${
                  Number(order.deliveryFeeEur) > 0
                    ? ` + ${order.deliveryFeeEur} € delivery`
                    : ""
                } · paid ${order.paidEur} €`
              : "No agreed price yet"}
          </span>
          {order.balanceDueEur && Number(order.balanceDueEur) > 0 ? (
            <span className="badge warning">
              {order.balanceDueEur} € outstanding
            </span>
          ) : null}
          {/* KI-104 (owner 2026-10-19): money beyond what is owed is accepted
              and shown, never hidden behind a €0 balance — before hand-over
              against the agreed goods + fee, after it against what was
              charged + fee (the server's one rule, `overpaidEur`). */}
          {/* G-166 (owner 2026-10-20): a web order is finished by "Hand to
              delivery" alone; once handed over it waits for its courier. */}
          {order.webAwaitsHandover ? (
            <span className="badge muted">Web order · hand over with &ldquo;Hand to delivery&rdquo;</span>
          ) : null}
          {order.webAwaitingCourier ? (
            <span className="badge primary">Handed over · waiting for the courier</span>
          ) : null}
          {/* G-177 (KI-89): the courier delivered, the sale could not be written. */}
          {order.deliveredNotCompleted ? (
            <span className="badge danger">Delivered · not completed</span>
          ) : null}
          {/* G-166 (owner 2026-10-20): marked paid, no money recorded. */}
          {order.prepaidNotRecorded ? (
            <span className="badge warning">Prepaid · not recorded</span>
          ) : null}
          {order.overpaidEur && Number(order.overpaidEur) > 0 ? (
            <span className="badge warning">Overpaid {order.overpaidEur} €</span>
          ) : null}
          {Number(order.refundedBeyondPaidEur) > 0 ? (
            <span className="badge danger">
              Refunded {order.refundedBeyondPaidEur} € more than paid
            </span>
          ) : null}
          {/* G-162: goods money given back on the order's sale. */}
          {order.sale && Number(order.sale.refundedEur) > 0 ? (
            <span className="badge muted">Refunded {order.sale.refundedEur} € on the sale</span>
          ) : null}
        </div>
        {order.shopifyOrder ? (
          <WebOrderMoney
            shopify={order.shopifyOrder}
            label={`Shopify${order.shopifyOrder.orderNumber ? ` ${order.shopifyOrder.orderNumber}` : ""}`}
          />
        ) : null}
        {order.payments.length > 0 ? (
          <ul className="order-detail-payments">
            {order.payments.map((pay) => (
              <li key={pay.id}>
                {fmt(pay.takenAt)} ·{" "}
                {pay.kind === "REFUND"
                  ? "refund "
                  : pay.kind === "REFUND_REMOVED"
                    ? "refund removed "
                    : ""}
                {pay.amountEur} €{" "}
                {pay.method ? PAYMENT_METHOD_LABELS[pay.method] : ""}
                {pay.takenBy ? ` · taken by ${pay.takenBy.fullName}` : ""}
                {pay.notes ? ` · ${pay.notes}` : ""}
              </li>
            ))}
          </ul>
        ) : null}

        {/* ── Photos — every photo this order accumulated, labeled ── */}
        {gallery.length > 0 ? (
          <div>
            <strong>Photos</strong>
            <div className="order-detail-gallery">
              {gallery.map((item) => (
                <figure key={item.key} className="order-detail-photo">
                  {item.url ? (
                    <img src={item.url} alt={item.label} />
                  ) : (
                    <div className="order-detail-photo-missing muted">
                      unavailable
                    </div>
                  )}
                  <figcaption>
                    {item.label}
                    {item.by ? ` · ${item.by}` : ""}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        ) : null}

        {/* ── Recipe ─────────────────────────────────────────────── */}
        {build ? (
          <div>
            <strong>
              Bouquet recipe
              {build.builtBy ? ` · built by ${build.builtBy.fullName}` : ""}
            </strong>
            <table className="order-detail-recipe">
              <thead>
                <tr>
                  <th>Qty</th>
                  <th>Item</th>
                  {seesRecipeMoney ? <th>Unit price</th> : null}
                </tr>
              </thead>
              <tbody>
                {build.lines.map((line) => (
                  <tr key={line.id}>
                    <td>
                      {Number(line.quantity)}
                      {line.uom ? ` ${line.uom.toLowerCase()}` : ""}
                    </td>
                    <td>{line.item?.name ?? "—"}</td>
                    {seesRecipeMoney ? (
                      <td>
                        {line.saleUnitPriceEur
                          ? `${line.saleUnitPriceEur} €`
                          : "—"}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            {seesRecipeMoney ? (
              <p className="muted" style={{ margin: 0 }}>
                {afterCostLabel("Cost to build", bouquetCostText(build, (n) => `${n.toFixed(2)} €`))}
                {build.salePriceEur ? ` · sold at ${build.salePriceEur} €` : ""}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* ── Timeline — every timestamp, every plane ────────────── */}
        <div>
          <strong>Timeline</strong>
          {events === null ? (
            <p className="muted" style={{ margin: 0 }}>
              Loading…
            </p>
          ) : events.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              No recorded events — this order predates the timeline
              (created {fmt(order.createdAt)}).
            </p>
          ) : (
            <ul className="order-detail-timeline">
              {events.map((event) => (
                <li key={event.id}>
                  <span className="muted">{fmt(event.createdAt)}</span>{" "}
                  {EVENT_LABELS[event.type] ?? event.type}
                  {event.type === "AMENDED" ? amendedDetail(event.metadata) : ""}
                  {event.actor
                    ? ` · ${event.actor.fullName}`
                    : viaShopify(event.metadata)
                      ? " · Shopify"
                      : ""}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Actions — the one shared row ───────────────────────── */}
        <OrderActions order={order} onChanged={onChanged} onError={setError} />
      </div>
    </Modal>
  );
}
